import { useState, useEffect } from "react";
import { AdminLayout } from "@/components/shared/AdminLayout";
import { DealerContentReview } from "@/components/shared/DealerContentReview";
import { useAdminAccess } from "@/lib/adminAccess";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  getGalleryItems,
  createGalleryItem,
  updateGalleryItem,
  deleteGalleryItem,
  uploadFile,
  getOembedData,
  getSocialMediaConfigs,
  updateSocialMediaConfig,
  syncSocialMedia,
} from "@/lib/api";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Image,
  Plus,
  Trash2,
  Loader2,
  Upload,
  Instagram,
  Facebook,
  Twitter,
  RefreshCw,
  Link2,
  Video,
  Linkedin,
  Send,
  Ghost,
} from "lucide-react";

const PLATFORM_HELP = {
  instagram: {
    label: "Instagram",
    fields: [
      { key: "access_token", label: "Access Token", placeholder: "Your Instagram Graph API access token", type: "password" },
      { key: "user_id", label: "Instagram User ID", placeholder: "e.g. 17841405793187218", type: "text" },
    ],
    instructions: [
      "Go to developers.facebook.com and create or select an app",
      "Add the Instagram Graph API product to your app",
      "Generate a long-lived access token via the Graph API Explorer",
      "Your Instagram User ID is shown in the API response when you query /me",
    ],
    docsUrl: "https://developers.facebook.com/docs/instagram-api/getting-started",
  },
  facebook: {
    label: "Facebook",
    fields: [
      { key: "access_token", label: "Page Access Token", placeholder: "Your Facebook Page access token", type: "password" },
      { key: "page_id", label: "Page ID", placeholder: "e.g. 123456789", type: "text" },
    ],
    instructions: [
      "Go to developers.facebook.com and create or select an app",
      "Generate a Page Access Token from the Graph API Explorer",
      "Find your Page ID in your Facebook Page's About section",
    ],
    docsUrl: "https://developers.facebook.com/docs/pages-api/getting-started",
  },
  twitter: {
    label: "Twitter / X",
    fields: [
      { key: "access_token", label: "Bearer Token", placeholder: "Your Twitter API bearer token", type: "password" },
      { key: "user_id", label: "Twitter Username", placeholder: "e.g. @yourhandle", type: "text" },
    ],
    instructions: [
      "Go to developer.twitter.com and create a project/app",
      "Generate a Bearer Token under Keys and Tokens",
    ],
    docsUrl: "https://developer.twitter.com/en/docs/getting-started",
  },
  tiktok: {
    label: "TikTok",
    fields: [
      { key: "access_token", label: "Access Token", placeholder: "Your TikTok API access token", type: "password" },
      { key: "user_id", label: "User Open ID", placeholder: "Your TikTok Open ID", type: "text" },
    ],
    instructions: [
      "Go to developers.tiktok.com and create an app",
      "Complete OAuth to get an access token",
    ],
    docsUrl: "https://developers.tiktok.com/doc/getting-started",
  },
  linkedin: {
    label: "LinkedIn",
    fields: [
      { key: "access_token", label: "Access Token", placeholder: "Your LinkedIn API access token", type: "password" },
      { key: "user_id", label: "Organization ID", placeholder: "Your LinkedIn company/org ID", type: "text" },
    ],
    instructions: [
      "Go to linkedin.com/developers and create an app",
      "Request the w_member_social or w_organization_social permission",
      "Generate an access token via OAuth 2.0",
    ],
    docsUrl: "https://learn.microsoft.com/en-us/linkedin/marketing/",
  },
  snapchat: {
    label: "Snapchat",
    fields: [
      { key: "access_token", label: "Access Token", placeholder: "Your Snapchat API access token", type: "password" },
    ],
    instructions: [
      "Go to business.snapchat.com and create a Business account",
      "Create an app in the Snap Kit developer portal",
      "Generate an access token",
    ],
    docsUrl: "https://developers.snap.com/",
  },
};

function SocialPlatformCard({ config, PlatformIcon, syncing, onToggle, onSync, onSave }) {
  const [expanded, setExpanded] = useState(false);
  const [token, setToken] = useState("");
  const [userId, setUserId] = useState("");
  const [pageId, setPageId] = useState("");
  const [saving, setSaving] = useState(false);
  const help = PLATFORM_HELP[config.platform] || { label: config.platform, fields: [], instructions: [] };

  const handleConnect = async () => {
    if (!token) return toast.error("Access token is required");
    setSaving(true);
    try {
      await onSave({
        platform: config.platform,
        enabled: true,
        access_token: token,
        user_id: userId || undefined,
        page_id: pageId || undefined,
      });
      toast.success(`${help.label} connected successfully`);
      setToken("");
      setExpanded(false);
    } catch {
      toast.error(`Failed to connect ${help.label}`);
    }
    setSaving(false);
  };

  const handleDisconnect = async () => {
    setSaving(true);
    try {
      await onSave({ platform: config.platform, enabled: false, access_token: "" });
      toast.success(`${help.label} disconnected`);
    } catch {
      toast.error(`Failed to disconnect ${help.label}`);
    }
    setSaving(false);
  };

  const isConnected = config.status === "connected" && config.enabled;

  return (
    <Card className={`border-slate-200 ${isConnected ? "border-green-200 bg-green-50/30" : ""}`} data-testid={`social-config-${config.platform}`}>
      <CardContent className="p-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isConnected ? "bg-green-100" : "bg-slate-100"}`}>
              <PlatformIcon className={`w-5 h-5 ${isConnected ? "text-green-600" : "text-slate-600"}`} />
            </div>
            <div>
              <h3 className="font-heading font-semibold text-navy capitalize">{help.label}</h3>
              <p className="text-xs text-slate-500 font-body">
                Status:{" "}
                <span className={isConnected ? "text-green-600 font-semibold" : "text-slate-400"}>
                  {isConnected ? "Connected" : "Not connected"}
                </span>
                {config.last_sync && (
                  <span className="ml-2">| Last sync: {new Date(config.last_sync).toLocaleString()}</span>
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isConnected && (
              <>
                <Button variant="outline" size="sm" onClick={onSync} disabled={syncing} data-testid={`social-sync-${config.platform}`}>
                  <RefreshCw className={`w-4 h-4 mr-1 ${syncing ? "animate-spin" : ""}`} /> Sync
                </Button>
                <Button variant="outline" size="sm" onClick={handleDisconnect} disabled={saving} className="text-red-600 hover:bg-red-50" data-testid={`social-disconnect-${config.platform}`}>
                  Disconnect
                </Button>
              </>
            )}
            {!isConnected && (
              <Button size="sm" className="bg-teal hover:bg-teal/90 text-white" onClick={() => setExpanded(!expanded)} data-testid={`social-connect-${config.platform}`}>
                <Link2 className="w-4 h-4 mr-1" /> Connect
              </Button>
            )}
          </div>
        </div>

        {expanded && !isConnected && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="mt-4 pt-4 border-t border-slate-200 space-y-4">
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-xs font-semibold text-slate-600 mb-2">How to get your credentials:</p>
              <ol className="text-xs text-slate-500 space-y-1 list-decimal list-inside">
                {help.instructions.map((step, i) => <li key={i}>{step}</li>)}
              </ol>
              {help.docsUrl && (
                <a href={help.docsUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-teal hover:underline mt-2 inline-block">
                  View official docs &rarr;
                </a>
              )}
            </div>
            {help.fields.map((field) => (
              <div key={field.key}>
                <Label className="text-xs text-slate-600">{field.label}</Label>
                <Input
                  type={field.type}
                  placeholder={field.placeholder}
                  value={field.key === "access_token" ? token : field.key === "user_id" ? userId : pageId}
                  onChange={(e) => {
                    if (field.key === "access_token") setToken(e.target.value);
                    else if (field.key === "user_id") setUserId(e.target.value);
                    else setPageId(e.target.value);
                  }}
                  data-testid={`social-${config.platform}-${field.key}`}
                />
              </div>
            ))}
            <div className="flex gap-2">
              <Button onClick={handleConnect} disabled={saving || !token} className="bg-teal hover:bg-teal/90 text-white" data-testid={`social-save-${config.platform}`}>
                {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Link2 className="w-4 h-4 mr-1" />}
                Connect {help.label}
              </Button>
              <Button variant="ghost" onClick={() => setExpanded(false)}>Cancel</Button>
            </div>
          </motion.div>
        )}
      </CardContent>
    </Card>
  );
}

export default function AdminGallery() {
  const [items, setItems] = useState([]);
  const [socialConfigs, setSocialConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [syncing, setSyncing] = useState({});
  const [activeTab, setActiveTab] = useState("gallery");
  const canManageGallery = useAdminAccess().can("gallery", "manage");
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    image_url: "",
    media_type: "image",
    media_url: "",
    embed_html: null,
    embed_platform: null,
    tags: [],
    active: true,
    auto_post: false,
  });
  const [tagsInput, setTagsInput] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [mediaTab, setMediaTab] = useState("image");
  const [embedUrl, setEmbedUrl] = useState("");
  const [embedLoading, setEmbedLoading] = useState(false);
  const [embedPreview, setEmbedPreview] = useState(null);
  const [editingItem, setEditingItem] = useState(null);

  const fetchData = async () => {
    try {
      const [galleryData, configsData] = await Promise.all([
        getGalleryItems(null, false),
        getSocialMediaConfigs(),
      ]);
      setItems(galleryData);
      setSocialConfigs(configsData);
    } catch (error) {
      console.error("Error fetching gallery data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleFileUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const result = await uploadFile(file, "gallery");
      const fileUrl = `${process.env.REACT_APP_BACKEND_URL}${result.url}`;
      const isVideo = file.type.startsWith("video/");
      if (isVideo) {
        setFormData({ ...formData, media_type: "video", media_url: fileUrl, image_url: "" });
      } else {
        setFormData({ ...formData, media_type: "image", image_url: fileUrl });
      }
      toast.success(`${isVideo ? "Video" : "Image"} uploaded successfully`);
    } catch (error) {
      toast.error("Failed to upload file");
    } finally {
      setUploading(false);
    }
  };

  const handleFetchEmbed = async () => {
    if (!embedUrl.trim()) return;
    setEmbedLoading(true);
    try {
      const data = await getOembedData(embedUrl);
      setEmbedPreview(data);
      setFormData({
        ...formData,
        media_type: "embed",
        media_url: embedUrl,
        embed_html: data.html,
        embed_platform: data.platform,
        image_url: data.thumbnail_url || "",
        title: formData.title || data.title || "",
      });
      if (data.html) {
        toast.success(`${data.platform || "Social"} link loaded`);
      } else {
        toast.info("Link saved — embed preview may not be available for all platforms");
        setFormData((prev) => ({ ...prev, media_type: "embed", media_url: embedUrl, embed_platform: data.platform || "link" }));
      }
    } catch {
      toast.error("Failed to fetch embed data");
    }
    setEmbedLoading(false);
  };

  const resetForm = () => {
    setFormData({ title: "", description: "", image_url: "", media_type: "image", media_url: "", embed_html: null, embed_platform: null, tags: [], active: true, auto_post: false });
    setTagsInput("");
    setSelectedFile(null);
    setMediaTab("image");
    setEmbedUrl("");
    setEmbedPreview(null);
    setEditingItem(null);
  };

  const openEditDialog = (item) => {
    setEditingItem(item);
    const mt = item.media_type || "image";
    setFormData({
      title: item.title || "",
      description: item.description || "",
      image_url: item.image_url || "",
      media_type: mt,
      media_url: item.media_url || "",
      embed_html: item.embed_html || null,
      embed_platform: item.embed_platform || null,
      tags: item.tags || [],
      active: item.active !== false,
      auto_post: false,
    });
    setTagsInput((item.tags || []).join(", "));
    setMediaTab(mt);
    setEmbedUrl(mt === "embed" ? (item.media_url || "") : "");
    setEmbedPreview(mt === "embed" ? { platform: item.embed_platform, html: item.embed_html, thumbnail_url: item.image_url, title: item.title } : null);
    setSelectedFile(null);
    setIsDialogOpen(true);
  };

  const handleSubmitItem = async (e) => {
    e.preventDefault();
    if (!formData.image_url && !formData.media_url) {
      toast.error("Please upload a file, paste a URL, or add a social media link");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        tags: tagsInput.split(",").map((t) => t.trim()).filter(Boolean),
        source: formData.media_type === "embed" ? (formData.embed_platform || "link") : "upload",
      };
      if (editingItem) {
        await updateGalleryItem(editingItem.id, payload);
        toast.success("Gallery item updated");
      } else {
        await createGalleryItem(payload);
        toast.success(formData.auto_post ? "Gallery item added & posted to social media" : "Gallery item added");
      }
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(editingItem ? "Failed to update gallery item" : "Failed to add gallery item");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm("Are you sure you want to delete this item?")) return;
    try {
      await deleteGalleryItem(itemId);
      toast.success("Gallery item deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete item");
    }
  };

  const handleSyncPlatform = async (platform) => {
    setSyncing({ ...syncing, [platform]: true });
    try {
      const result = await syncSocialMedia(platform);
      toast.success(result.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to sync ${platform}`);
    } finally {
      setSyncing({ ...syncing, [platform]: false });
    }
  };

  const handleUpdateConfig = async (platform, configData) => {
    try {
      await updateSocialMediaConfig(platform, configData);
      toast.success(`${platform} configuration updated`);
      fetchData();
    } catch (error) {
      toast.error(`Failed to update ${platform} config`);
    }
  };

  const platformIcons = {
    instagram: Instagram,
    facebook: Facebook,
    twitter: Twitter,
    tiktok: Video,
    linkedin: Linkedin,
    snapchat: Ghost,
  };

  const connectedPlatforms = socialConfigs.filter(
    (c) => c.enabled && c.status === "connected"
  );

  return (
    <AdminLayout title="Gallery Management" breadcrumb="Gallery">
      <div className="space-y-6" data-testid="admin-gallery-page">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="flex justify-between items-center">
            <TabsList>
              <TabsTrigger value="gallery">Gallery Items</TabsTrigger>
              <TabsTrigger value="social">Social Media</TabsTrigger>
              <TabsTrigger value="dealer-content" data-testid="gallery-tab-dealer-content">Dealer Submissions</TabsTrigger>
            </TabsList>
            {activeTab === "gallery" && (
              <Button
                onClick={() => { resetForm(); setIsDialogOpen(true); }}
                className="bg-teal hover:bg-teal-dark text-white"
                data-testid="add-gallery-item-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Media
              </Button>
            )}
          </div>

          {/* Gallery Items Tab */}
          <TabsContent value="gallery" className="mt-6">
            {loading ? (
              <p className="text-slate-500 text-center py-8 font-body">Loading gallery...</p>
            ) : items.length === 0 ? (
              <div className="text-center py-12">
                <Image className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500 font-body">No gallery items yet</p>
                <p className="text-slate-400 text-sm font-body">Upload your first photo to get started</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {items.map((item, index) => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.03 }}
                  >
                    <Card className="border-slate-200 overflow-hidden group" data-testid={`gallery-admin-item-${item.id}`}>
                      <div className="aspect-square relative bg-slate-100">
                        {item.media_type === "video" ? (
                          <video src={item.media_url || item.image_url} className="w-full h-full object-cover" muted />
                        ) : item.media_type === "embed" ? (
                          item.image_url ? (
                            <img src={item.image_url} alt={item.title || "Embed"} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex flex-col items-center justify-center bg-slate-800 text-white">
                              <Link2 className="w-10 h-10 mb-2 opacity-60" />
                              <span className="text-xs opacity-60 capitalize">{item.embed_platform || item.source}</span>
                            </div>
                          )
                        ) : (
                          <img src={item.image_url} alt={item.title || "Gallery"} className="w-full h-full object-cover" />
                        )}
                        {!item.active && (
                          <div className="absolute top-2 left-2 px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">Hidden</div>
                        )}
                        <div className="absolute top-2 right-2 flex gap-1">
                          {(item.media_type === "video" || item.media_type === "embed") && (
                            <span className="px-2 py-0.5 bg-teal/80 text-white text-xs rounded-full capitalize">{item.media_type === "embed" ? (item.embed_platform || "link") : "video"}</span>
                          )}
                          <span className="px-2 py-0.5 bg-black/60 text-white text-xs rounded-full capitalize">{item.source}</span>
                        </div>
                      </div>
                      <CardContent className="p-3">
                        <p className="text-sm font-heading font-semibold text-navy truncate">{item.title || "Untitled"}</p>
                        <p className="text-xs text-slate-500 font-body mt-1">{new Date(item.created_at).toLocaleDateString()}</p>
                        <div className="flex gap-2 mt-2">
                          <Button variant="outline" size="sm" onClick={() => openEditDialog(item)} className="flex-1" data-testid={`edit-gallery-item-${item.id}`}>
                            Edit
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => handleDeleteItem(item.id)} className="flex-1 text-red-600 hover:bg-red-50" data-testid={`delete-gallery-item-${item.id}`}>
                            <Trash2 className="w-3 h-3 mr-1" /> Delete
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Social Media Tab */}
          <TabsContent value="dealer-content" className="mt-6">
            <DealerContentReview canManage={canManageGallery} />
          </TabsContent>
          <TabsContent value="social" className="mt-6">
            <div className="space-y-4">
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-amber-800 text-sm font-body">
                  Connect your social media accounts to sync photos to the gallery and enable auto-posting.
                  When you upload a photo with auto-post enabled, it will be published to all connected platforms automatically.
                </p>
              </div>
              {socialConfigs.map((config) => {
                const PlatformIcon = platformIcons[config.platform] || Link2;
                return (
                  <SocialPlatformCard
                    key={config.id || config.platform}
                    config={config}
                    PlatformIcon={PlatformIcon}
                    syncing={syncing[config.platform]}
                    onToggle={(checked) => handleUpdateConfig(config.platform, { platform: config.platform, enabled: checked })}
                    onSync={() => handleSyncPlatform(config.platform)}
                    onSave={(data) => handleUpdateConfig(config.platform, data)}
                  />
                );
              })}
            </div>
          </TabsContent>
        </Tabs>

        {/* Add Gallery Item Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-navy">{editingItem ? "Edit Gallery Item" : "Add to Gallery"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmitItem} className="space-y-4" data-testid="gallery-item-form">
              <div>
                <Label htmlFor="gallery-title">Title</Label>
                <Input
                  id="gallery-title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Title (optional)"
                  data-testid="gallery-title-input"
                />
              </div>
              <div>
                <Label htmlFor="gallery-description">Description</Label>
                <Textarea
                  id="gallery-description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={2}
                  placeholder="Optional description"
                  data-testid="gallery-description-input"
                />
              </div>

              {/* Media Type Tabs */}
              <div className="space-y-3">
                <Label>Media</Label>
                <Tabs value={mediaTab} onValueChange={(v) => { setMediaTab(v); setFormData({ ...formData, media_type: v, image_url: "", media_url: "", embed_html: null, embed_platform: null }); setSelectedFile(null); setEmbedUrl(""); setEmbedPreview(null); }}>
                  <TabsList className="w-full grid grid-cols-3">
                    <TabsTrigger value="image" data-testid="media-tab-image"><Image className="w-3.5 h-3.5 mr-1.5" />Image</TabsTrigger>
                    <TabsTrigger value="video" data-testid="media-tab-video"><Video className="w-3.5 h-3.5 mr-1.5" />Video</TabsTrigger>
                    <TabsTrigger value="embed" data-testid="media-tab-embed"><Link2 className="w-3.5 h-3.5 mr-1.5" />Social Link</TabsTrigger>
                  </TabsList>

                  {/* Image Tab */}
                  <TabsContent value="image" className="mt-3 space-y-3">
                    <div className="border-2 border-dashed border-slate-300 rounded-lg p-4 text-center">
                      <input type="file" accept="image/*" onChange={(e) => { const f = e.target.files[0]; if (f) { setSelectedFile(f); handleFileUpload(f); } }} className="hidden" id="gallery-file-input" data-testid="gallery-file-input" />
                      <label htmlFor="gallery-file-input" className="cursor-pointer">
                        {uploading ? (
                          <div className="flex items-center justify-center gap-2"><Loader2 className="w-5 h-5 animate-spin text-teal" /><span className="text-sm text-slate-600">Uploading...</span></div>
                        ) : (
                          <><Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" /><p className="text-sm text-slate-600">Click to upload an image</p></>
                        )}
                      </label>
                    </div>
                    <Input value={formData.image_url} onChange={(e) => setFormData({ ...formData, image_url: e.target.value })} placeholder="Or paste image URL here" data-testid="gallery-image-url-input" />
                    {formData.image_url && (
                      <div className="rounded-lg overflow-hidden bg-slate-100 max-h-48"><img src={formData.image_url} alt="Preview" className="w-full h-48 object-cover" onError={(e) => (e.target.style.display = "none")} /></div>
                    )}
                  </TabsContent>

                  {/* Video Tab */}
                  <TabsContent value="video" className="mt-3 space-y-3">
                    <div className="border-2 border-dashed border-slate-300 rounded-lg p-4 text-center">
                      <input type="file" accept="video/*" onChange={(e) => { const f = e.target.files[0]; if (f) { setSelectedFile(f); handleFileUpload(f); } }} className="hidden" id="gallery-video-input" data-testid="gallery-video-input" />
                      <label htmlFor="gallery-video-input" className="cursor-pointer">
                        {uploading ? (
                          <div className="flex items-center justify-center gap-2"><Loader2 className="w-5 h-5 animate-spin text-teal" /><span className="text-sm text-slate-600">Uploading...</span></div>
                        ) : (
                          <><Video className="w-8 h-8 text-slate-400 mx-auto mb-2" /><p className="text-sm text-slate-600">Click to upload a video (MP4)</p></>
                        )}
                      </label>
                    </div>
                    <Input value={formData.media_url} onChange={(e) => setFormData({ ...formData, media_url: e.target.value, media_type: "video" })} placeholder="Or paste video URL here" data-testid="gallery-video-url-input" />
                    {formData.media_url && formData.media_type === "video" && (
                      <div className="rounded-lg overflow-hidden bg-slate-900 max-h-48">
                        <video src={formData.media_url} controls className="w-full h-48 object-contain" />
                      </div>
                    )}
                  </TabsContent>

                  {/* Social Link / Embed Tab */}
                  <TabsContent value="embed" className="mt-3 space-y-3">
                    <p className="text-xs text-slate-500">Paste a link from TikTok, Instagram, YouTube, Facebook, or Twitter/X</p>
                    <div className="flex gap-2">
                      <Input value={embedUrl} onChange={(e) => setEmbedUrl(e.target.value)} placeholder="https://www.tiktok.com/@user/video/..." className="flex-1" data-testid="gallery-embed-url-input" />
                      <Button type="button" onClick={handleFetchEmbed} disabled={embedLoading || !embedUrl.trim()} className="bg-teal hover:bg-teal/90 text-white" data-testid="gallery-fetch-embed-btn">
                        {embedLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Load"}
                      </Button>
                    </div>
                    {embedPreview && (
                      <div className="border border-slate-200 rounded-lg p-3 bg-slate-50 space-y-2" data-testid="embed-preview">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold text-teal capitalize bg-teal/10 px-2 py-0.5 rounded-full">{embedPreview.platform || "link"}</span>
                          {embedPreview.author_name && <span className="text-xs text-slate-500">{embedPreview.author_name}</span>}
                        </div>
                        {embedPreview.title && <p className="text-sm text-slate-700 line-clamp-2">{embedPreview.title}</p>}
                        {embedPreview.thumbnail_url && (
                          <div className="rounded overflow-hidden max-h-40"><img src={embedPreview.thumbnail_url} alt="" className="w-full h-40 object-cover" /></div>
                        )}
                        {embedPreview.html && (
                          <p className="text-xs text-green-600 flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500 inline-block" />Embed ready</p>
                        )}
                      </div>
                    )}
                  </TabsContent>
                </Tabs>
              </div>

              <div>
                <Label htmlFor="gallery-tags">Tags (comma separated)</Label>
                <Input
                  id="gallery-tags"
                  value={tagsInput}
                  onChange={(e) => setTagsInput(e.target.value)}
                  placeholder="store, event, team"
                  data-testid="gallery-tags-input"
                />
              </div>
              <div className="flex items-center gap-3">
                <Switch
                  id="gallery-active"
                  checked={formData.active}
                  onCheckedChange={(checked) => setFormData({ ...formData, active: checked })}
                  data-testid="gallery-active-switch"
                />
                <Label htmlFor="gallery-active">Visible on website</Label>
              </div>

              {/* Auto-post to Social Media */}
              <div className="border border-slate-200 rounded-lg p-4 space-y-3" data-testid="auto-post-section">
                <div className="flex items-center gap-3">
                  <Switch
                    id="gallery-auto-post"
                    checked={formData.auto_post}
                    onCheckedChange={(checked) => setFormData({ ...formData, auto_post: checked })}
                    data-testid="gallery-auto-post-switch"
                  />
                  <Label htmlFor="gallery-auto-post" className="flex items-center gap-2">
                    <Send className="w-4 h-4 text-teal" />
                    Auto-post to connected social media
                  </Label>
                </div>
                {formData.auto_post && (
                  <div className="pl-8">
                    {connectedPlatforms.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {connectedPlatforms.map((c) => {
                          const Icon = platformIcons[c.platform] || Link2;
                          return (
                            <span
                              key={c.platform}
                              className="flex items-center gap-1.5 px-2.5 py-1 bg-green-50 border border-green-200 rounded-full text-xs text-green-700 capitalize"
                              data-testid={`auto-post-platform-${c.platform}`}
                            >
                              <Icon className="w-3.5 h-3.5" />
                              {c.platform}
                            </span>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-xs text-amber-600">
                        No social media accounts connected. Go to the Social Media tab to connect accounts.
                      </p>
                    )}
                  </div>
                )}
              </div>

              <Button
                type="submit"
                disabled={submitting || uploading}
                className="w-full bg-teal hover:bg-teal-dark text-white"
                data-testid="submit-gallery-item-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {editingItem ? "Saving..." : "Adding..."}
                  </>
                ) : (
                  editingItem ? "Save Changes" : "Add to Gallery"
                )}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
